import React, { useEffect, useState } from 'react';
import { X, Bell, AlertTriangle, Info, Car, Camera, Users } from 'lucide-react';
import type { Notification, NotificationEventType, NotificationPriority } from '../../types/notifications';

interface ToastNotificationProps {
  notification: Notification;
  onClose: () => void;
  onClick?: () => void;
  duration?: number;
}

const getIcon = (eventType: NotificationEventType) => {
  switch (eventType) {
    case 'motion':
      return <Users size={20} />;
    case 'lpr':
      return <Car size={20} />;
    case 'intrusion':
      return <AlertTriangle size={20} />;
    case 'entry_exit':
      return <Car size={20} />;
    case 'camera_alert':
      return <Camera size={20} />;
    case 'system_alert':
      return <Info size={20} />;
    default:
      return <Bell size={20} />;
  }
};

const getPriorityStyles = (priority: NotificationPriority): { bg: string; border: string; icon: string } => {
  switch (priority) {
    case 'urgent':
      return {
        bg: 'bg-red-500/10',
        border: 'border-red-500',
        icon: 'text-red-500',
      };
    case 'high':
      return {
        bg: 'bg-orange-500/10',
        border: 'border-orange-500',
        icon: 'text-orange-500',
      };
    case 'normal':
      return {
        bg: 'bg-blue-500/10',
        border: 'border-blue-500',
        icon: 'text-blue-500',
      };
    case 'low':
      return {
        bg: 'bg-gray-500/10',
        border: 'border-gray-500',
        icon: 'text-gray-500',
      };
    default:
      return {
        bg: 'bg-blue-500/10',
        border: 'border-blue-500',
        icon: 'text-blue-500',
      };
  }
};

export const ToastNotification: React.FC<ToastNotificationProps> = ({
  notification,
  onClose,
  onClick,
  duration = 5000,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const styles = getPriorityStyles(notification.priority);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose();
    }, 300); // Match animation duration
  };

  useEffect(() => {
    // Trigger enter animation
    requestAnimationFrame(() => {
      setIsVisible(true);
    });

    // Auto-dismiss (unless urgent)
    if (notification.priority !== 'urgent' && duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration, notification.priority]);

  return (
    <div
      className={`
        relative max-w-sm w-full rounded-lg shadow-lg border-l-4
        ${styles.bg} ${styles.border}
        backdrop-blur-sm
        transform transition-all duration-300 ease-out
        ${isVisible && !isLeaving ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
        cursor-pointer hover:shadow-xl
      `}
      onClick={onClick}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className={`flex-shrink-0 ${styles.icon}`}>
            {getIcon(notification.event_type)}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-text-primary">
              {notification.title}
            </p>
            <p className="text-sm text-text-muted mt-1 line-clamp-2">
              {notification.body}
            </p>
            {notification.data?.license_plate && (
              <span className="inline-block mt-2 px-2 py-0.5 bg-surface-secondary rounded text-xs font-mono text-text-primary">
                {notification.data.license_plate}
              </span>
            )}
          </div>

          {/* Close button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            className="flex-shrink-0 p-1 rounded hover:bg-white/10 text-text-muted hover:text-text-primary transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Time */}
        <div className="mt-2 text-xs text-text-muted">
          {formatTimeAgo(notification.createdAt)}
        </div>
      </div>

      {/* Progress bar for auto-dismiss */}
      {notification.priority !== 'urgent' && duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/10 rounded-b-lg overflow-hidden">
          <div
            className={`h-full ${styles.icon.replace('text-', 'bg-')} opacity-50`}
            style={{
              animation: `shrink ${duration}ms linear forwards`,
            }}
          />
        </div>
      )}

      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};

// Toast Container Component
interface ToastContainerProps {
  notifications: Notification[];
  onClose: (id: string) => void;
  onClick?: (notification: Notification) => void;
  maxVisible?: number;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({
  notifications,
  onClose,
  onClick,
  maxVisible = 5,
}) => {
  const visibleNotifications = notifications.slice(0, maxVisible);

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-3 pointer-events-none">
      {visibleNotifications.map((notification) => (
        <div key={notification.id} className="pointer-events-auto">
          <ToastNotification
            notification={notification}
            onClose={() => onClose(notification.id)}
            onClick={() => onClick?.(notification)}
          />
        </div>
      ))}
    </div>
  );
};

// Helper function to format time
const formatTimeAgo = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);

  if (diffSec < 10) return 'Just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  
  return date.toLocaleDateString();
};

export default ToastNotification;
